import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const statusClass = {
  active: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  ended: 'bg-gray-100 text-gray-700',
};

const Discovery = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/api/admin/discovery/sessions`)
      .then((response) => {
        if (!cancelled) setSessions(response.data.data.sessions || []);
      })
      .catch((error) => {
        console.error('Error fetching discovery sessions:', error);
        toast.error(error.response?.data?.error || 'Discovery master is unreachable');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const remove = async (session) => {
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/discovery/sessions/${session.id}`);
      setSessions((current) => current.filter((item) => item.id !== session.id));
      setConfirmId('');
      toast.success('Session deleted');
    } catch (error) {
      toast.error(error.response?.data?.error || 'The session could not be deleted.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Discovery</h1>
          <p className="mt-1 max-w-xl text-sm text-gray-500">
            One master keeps every customer swarm in its own session. Open a session before you send an installer.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/discovery/settings"
            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
          >
            Settings
          </Link>
          <Link
            to="/discovery/new"
            className="inline-flex items-center justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            Open a session
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="mt-16">
          <LoadingSpinner />
        </div>
      ) : sessions.length === 0 ? (
        <div className="mt-8 rounded-lg border border-dashed border-gray-300 bg-white px-5 py-10">
          <h2 className="text-lg font-medium text-gray-900">No engagements yet</h2>
          <p className="mt-2 max-w-xl text-sm text-gray-500">
            Open a customer session, then download its enrollment file for the computers that will be observed.
          </p>
        </div>
      ) : (
        <ul className="mt-8 divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white">
          {sessions.map((session) => (
            <li key={session.id}>
              <div className="flex items-center gap-3 px-5 py-4 hover:bg-gray-50">
                {session.logoUrl ? (
                  <img
                    src={session.logoUrl}
                    alt=""
                    className="h-10 w-10 shrink-0 rounded-md border border-gray-200 bg-white object-contain p-1"
                  />
                ) : null}
                <Link to={`/discovery/${session.id}`} className="min-w-0 flex-1">
                  <span className="block font-medium text-gray-900">{session.companyName}</span>
                  <span className="text-sm text-gray-500">
                    Through {session.endDate} · {session.agentCount} computers · {session.cardsSent} cards
                  </span>
                </Link>
                <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass[session.status] || statusClass.ended}`}>
                  {session.status}
                </span>
                <button
                  type="button"
                  className="text-sm font-medium text-gray-400 hover:text-red-700"
                  onClick={() => setConfirmId(confirmId === session.id ? '' : session.id)}
                >
                  Delete
                </button>
              </div>
              {confirmId === session.id ? (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-red-100 bg-red-50 px-5 py-3">
                  <p className="text-sm text-red-900">
                    Delete {session.companyName}? Agents, cards, and the customer read link are removed.
                  </p>
                  <span className="flex gap-2">
                    <button
                      type="button"
                      className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-white"
                      onClick={() => setConfirmId('')}
                      disabled={deleting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="rounded-md bg-red-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50"
                      onClick={() => remove(session)}
                      disabled={deleting}
                    >
                      {deleting ? 'Deleting…' : 'Delete session'}
                    </button>
                  </span>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </AdminLayout>
  );
};

export default Discovery;
